/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Inject, Input, NgZone, OnChanges} from '@angular/core';
import {handleCopyModalResult} from '../common/util/handle-copy-modal-result';
import {ToastrService} from 'ngx-toastr';

import {AngularModalService} from '../common/modal/angular-modal.service';
import {wireModelCommsModelActions} from '../common/controller/model-comms-actions';
import {OBJECT_TYPE} from '../common/constants/object-type';
import {LineItemCopyModalComponent} from './line-item-copy-modal.component';
import template from './line-item-context-actions.component.html';
import {StarService} from '../../components/star/star.service';
import {StateService} from '@uirouter/core';

@Component({
  standalone: false,
  selector: 'adn-line-item-context-actions',
  template
})
export class LineItemContextActionsComponent implements OnChanges {
  @Input() model: any;
  @Input() modelComms: any;
  @Input() isNew: boolean;
  @Input() readOnly: boolean;
  @Input() viewType: number;

  readonly confirmation = {
    warningMessage: "The {{action}} of the Line Item will also stop it from serving ads.",
    object: 'Line Item'
  };

  isStarred = false;

  constructor(
    private readonly $state: StateService,
    private readonly toastr: ToastrService,
    @Inject(StarService) private readonly StarService: any,
    private readonly angularModalService: AngularModalService,
    private readonly ngZone: NgZone
  ) {
  }

  get objectId(): string {
    return this.model?.id;
  }

  get objectType(): string {
    return OBJECT_TYPE.LineItem.type;
  }

  ngOnChanges(): void {
    if (this.model && this.modelComms) {
      wireModelCommsModelActions(this.modelComms);
    }
    this.refreshStar();
  }

  copy(): void {
    if (this.readOnly || !this.model) {
      return;
    }

    const modalRef = this.angularModalService.show(LineItemCopyModalComponent, {
      initialState: {model: this.model},
      class: 'modal-lg'
    });
    (modalRef.content as LineItemCopyModalComponent).onCopied.subscribe((result) => {
      handleCopyModalResult(() => {
        if (result.destination) {
          this.$state.go('app.adv.line-items.line-item', {id: result.lineItemId}, {inherit: false});
        } else {
          this.toastr.success('A copy of this line item was successfully created.', 'Line Item Copied');
        }
      });
    });
  }

  toggleStar(): void {
    if (!this.objectId) {
      return;
    }
    const newStarred = !this.isStarred;
    this.StarService.updateStar(this.objectId, OBJECT_TYPE.LineItem, newStarred).then(() => {
      this.ngZone.run(() => {
        this.isStarred = newStarred;
      });
    });
  }

  private refreshStar(): void {
    if (!this.objectId) {
      this.isStarred = false;
      return;
    }
    this.isStarred = this.StarService.isStarred(this.objectId, OBJECT_TYPE.LineItem);
  }
}
